import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ProductList } from './ProductList';
import { Sidebar } from './ui/Sidebar';
import { MobileFilters } from './ui/MobileFilters';
import { ViewToggle } from './ui/ViewToggle';
import { DollarSign } from 'lucide-react';

const PRODUCT_TAG_LABELS = {
  best_seller: 'Best Sellers',
  new_release: 'New Releases',
  prime: 'Prime Deals',
  hot_deal: 'Today\'s Hot Deals',
  price_drop: 'Price Drops',
  trending: 'Trending Now',
  most_wished: 'Most Wished For',
  gift_idea: 'Gift Ideas',
};

export function TagPage() {
  const { tagSlug } = useParams();
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    productType: 'all',
    productTags: tagSlug,
    minPrice: '',
    maxPrice: '',
    stockStatus: '',
    sort: 'featured'
  });

  const [view, setView] = useState('list');

  // Update filters when tagSlug changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      productTags: [tagSlug]
    }));
  }, [tagSlug]);

  const tagName = PRODUCT_TAG_LABELS[tagSlug] || tagSlug;
  const title = `${tagName} - Best Tech Deals & Reviews ${new Date().getFullYear()}`;
  const description = `Browse our collection of products under "${tagName}". Discover top-rated deals and reviews tailored for ${tagName.toLowerCase()}.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`tech, ${tagSlug}, deals, reviews`} />
      </Helmet>

      <div className="min-h-screen">
        <header className="w-full">
          <div className="max-w-7xl mx-auto px-4 pt-8">
            <div className="flex justify-between items-center bg-white dark:bg-slate-800 rounded-lg shadow-sm hover:shadow-md transition-shadow px-6 py-8">
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">{tagName}</h1>
                <p className="mt-2 text-gray-600">Products related to "{tagName}"</p>
              </div>
              <ViewToggle view={view} onViewChange={setView} />
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* SEO Internal Links - Price Filter Variants */}
          <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <DollarSign className="w-4 h-4 mr-2" />
              {tagName} by Budget
            </h2>
            <div className="flex flex-wrap gap-2">
              <Link 
                to={`/tag/${tagSlug}/filter?max=300`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                Under $300
              </Link>
              <Link 
                to={`/tag/${tagSlug}/filter?min=300&max=600`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                $300 - $600
              </Link>
              <Link 
                to={`/tag/${tagSlug}/filter?min=600&max=1000`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                $600 - $1000
              </Link>
              <Link 
                to={`/tag/${tagSlug}/filter?min=1000`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                Over $1000
              </Link>
            </div>
          </div>

          <div className="flex gap-8">
            <Sidebar filters={filters} onFilterChange={setFilters} />
            <div className="w-full flex-grow">
              <ProductList filters={filters} view={view} />
            </div>
          </div>
        </main>

        <MobileFilters filters={filters} onFilterChange={setFilters} />
      </div>
    </>
  );
}
